<script setup>
import FileInput from './FileInput.vue';
import AssetCard from './AssetCard.vue';
import MediaGroup from './MediaGroup.vue';
import ButtonSecondary from '../ButtonSecondary.vue';
import { ref, computed, useTemplateRef, nextTick } from 'vue';
import { useSortable } from '@vueuse/integrations/useSortable'
import { UseMouseInElement } from '@vueuse/components';
import { useEventListener } from '@vueuse/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
  action: {
    type: String,
    required: true
  },
  backUrl: {
    type: String,
    default: '/assets'
  },
  method: {
    type: String,
    default: 'POST'
  },
  model: {
    type: Object,
    default: null
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  oldInput: {
    type: Object,
    default: () => ({})
  }
});

const newGroups = ref([]);
const existingGroups = ref(props.model?.media_groups || []);

const allCurrentGroups = computed(() => {
  return [...existingGroups.value, ...newGroups.value]
    .sort((a, b) => a.order - b.order);
});

const deletedGroups = computed(() => {
  return existingGroups.value.filter(group => group.willBeDeleted);
});

const newFiles = ref([{id: 'newfile-1', group_id: null}]);
const existingMedia = ref(props.model?.asset_library || []);

const uploadedFiles = computed(() => {
  return newFiles.value.filter(newFile => newFile.file);
});

const allCurrentAssets = computed(() => {
  // console.log('existingMedia', existingMedia.value);
  // console.log('uploadedFiles', uploadedFiles.value);
  return [...existingMedia.value, ...uploadedFiles.value]
    .filter(asset => !asset.willBeDeleted)
    .sort((a, b) => {
      return a.order - b.order;
    });
});

const ungroupedAssets = computed(() => {
  // console.log('allCurrentAssets', allCurrentAssets.value)
  return allCurrentAssets.value.filter(asset => !asset.group_id);
});

const deletedAssets = computed(() => {
  return existingMedia.value.filter(asset => asset.willBeDeleted);
});

function groupAdded() {
  const newGroup = {
    id: getNextNewGroupId(),
    name: `New Group ${newGroups.value.length + 1}`,
  };

  newGroups.value.push(newGroup);

  orderGroups(newGroup)
}

function getNextNewGroupId() {
  if( newGroups.value.length === 0 ) {
    return 'newgroup-1';
  }
  const maxId = Math.max(
    ...newGroups.value.map(file => {
      const match = file.id.match(/\d+$/);
      return match ? parseInt(match[0]) : 0;
    })
  );
  return `newgroup-${maxId + 1}`;
}

function toggleGroupRemoval(group) {
  group.willBeDeleted = !group.willBeDeleted;
}

function fileAdded(id, file) {
  const entry = newFiles.value.find(f => f.id === id);
  if (!entry) {
    console.error('newFiles entry not found for id:', id);
    return;
  }

  entry.file = file;

  orderAssets(entry);

  newFiles.value.push({
    id: getNextNewFileId(),
    group_id: null,
  });
}

function getNextNewFileId() {
  const maxId = Math.max(
    ...newFiles.value.map(file => {
      const match = file.id.match(/\d+$/);
      return match ? parseInt(match[0]) : 0;
    })
  );
  return `newfile-${maxId + 1}`;
}

function removeAsset(asset) {
  const fileIndex = newFiles.value.findIndex(f => f.id === asset.id);
  const media = existingMedia.value.find(m => m.id === asset.id);

  if (fileIndex !== -1) {
    newFiles.value.splice(fileIndex, 1);
  } else if (media) {
    media.willBeDeleted = true;
  }
}

function restoreAsset(asset) {
  const media = existingMedia.value.find(m => m.id === asset.id);

  if (media) {
    media.willBeDeleted = false;
    orderAssets(media);
    console.log(media.order, 'restored');
  }
}

function orderAssets(asset, zeroIndexedPosition=99999) {
  const assetsInGroup = allCurrentAssets.value.filter(a => a.group_id === asset.group_id);
  console.log('assetsInGroup', assetsInGroup);
  orderThings(assetsInGroup, asset, zeroIndexedPosition);
}

function orderGroups(group, zeroIndexedPosition=99999) {
  orderThings(allCurrentGroups.value, group, zeroIndexedPosition);
}

function orderThings(things, thing, newPosition) {
  // Clone the array
  const clonedThings = [...things];

  // Find and remove the target thing
  const thingIndex = clonedThings.findIndex(clone => clone.id === thing.id);
  if (thingIndex === -1) return;

  const [target] = clonedThings.splice(thingIndex, 1);

  // Insert at new position
  clonedThings.splice(newPosition, 0, target);

  // Update order values to match array positions
  clonedThings.forEach((clone, index) => {
    clone.order = index;
  });

  // Update the originals
  things.forEach(original => {
    const updated = clonedThings.find(clone => clone.id === original.id);
    if (updated) original.order = updated.order;
  });
}

const groupsRef = useTemplateRef('groupsRef');

useSortable(groupsRef, allCurrentGroups, {
  onUpdate: (e) => {
    const target = allCurrentGroups.value[e.oldIndex];
    nextTick(() => {
      orderGroups(target, e.newIndex);
    })
  },
  handle: '.sort-handle',
  animation: 300,
  dragClass: 'opacity-25',
  ghostClass: '!bg-blue-100',
  chosenClass: 'opacity-25',
});

const activeDropZoneGroupId = ref(undefined)

function assetDragged(asset, groupId){
  activeDropZoneGroupId.value = groupId
}

</script>


<template>
  <div class="mt-8">
    <!-- prevents form submission when Enter is pressed -->
    <button type="submit" disabled style="display: none" aria-hidden="true"></button>

    <label
      class="block mb-2 text-center text-sm leading-5 font-medium text-gray-700 sm:mt-px sm:pt-2"
    >
      Asset Library
    </label>

    <ButtonSecondary
      dusk="create-group"
      @click.stop.prevent="groupAdded"
    >
      New Group
    </ButtonSecondary>

    <div
      ref="groupsRef"
      class="my-8 divide-y divide-gray-200 overflow-hidden rounded-lg bg-gray-200 shadow-sm sm:grid sm:grid-cols-2 sm:gap-px sm:divide-y-0"
    >
      <div
        v-for="(group, index) in allCurrentGroups"
        :key="group.id"
        :data-group-id="group.id"
        class="relative bg-white p-6 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-inset sm:rounded-tr-none"
        :class="{
          'rounded-bl-lg sm:rounded-bl-none': index % 2 === 0,
          'rounded-br-lg sm:rounded-br-none': index % 2 !== 0,
          '!border-4': activeDropZoneGroupId == group.id,
          '!border-green-100': activeDropZoneGroupId == group.id,
          'border-dashed': activeDropZoneGroupId == group.id,
        }"
      >
        <FontAwesomeIcon
          v-if="activeDropZoneGroupId == group.id"
          :icon="['fas', 'fa-down-to-bracket']"
          class="absolute h-full inset-0 m-auto text-green-400 opacity-25 z-10"
        />
        <MediaGroup
          :group="group"
          :allAssets="allCurrentAssets"
          @removeGroup="toggleGroupRemoval"
          @restoreGroup="toggleGroupRemoval"
          @orderAssets="orderAssets"
          v-slot="{ assets }"
        >
          <AssetCard
            v-for="asset in assets"
            :key="asset.id"
            :asset="asset"
            @removeAsset="removeAsset"
            @dragMove="assetDragged"
          />
        </MediaGroup>
      </div>
    </div>

    <div>
      <input
        v-for="(group, index) in deletedGroups"
        :key="group.id"
        type="hidden"
        :name="`deletedMediaGroups[]`"
        :value="group.id"
      />
    </div>

    <div class="mb-8">
      <div class="text-center text-sm leading-5 font-medium text-gray-700 sm:mt-px sm:pt-2 mb-4">
        Ungrouped Assets
      </div>
      <div>
        <div
          data-group-id="null"
          class="relative bg-white p-6 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-inset sm:rounded-tr-none"
          :class="{
            'rounded-bl-lg sm:rounded-bl-none': index % 2 === 0,
            'rounded-br-lg sm:rounded-br-none': index % 2 !== 0,
            '!border-4': activeDropZoneGroupId === null,
            '!border-green-100': activeDropZoneGroupId === null,
            'border-dashed': activeDropZoneGroupId === null,
          }"
        >
          <FontAwesomeIcon
            v-if="activeDropZoneGroupId === null"
            :icon="['fas', 'fa-down-to-bracket']"
            class="absolute h-full inset-0 m-auto text-green-400 opacity-25 z-10"
          />
          <MediaGroup
            :allAssets="allCurrentAssets"
            @orderAssets="orderAssets"
            v-slot="{ assets }"
          >
            <AssetCard
              v-for="asset in assets"
              :key="asset.id"
              :asset="asset"
              @removeAsset="removeAsset"
              @dragMove="assetDragged"
            />
          </MediaGroup>
        </div>
      </div>
    </div>


    <div ref="dropzone">
      <FileInput
        v-for="newFile in newFiles"
        :key="newFile.id"
        :data="newFile"
        @file-added="fileAdded"
        class="m-auto"
      />
    </div>


    <div>
      <div
        v-if="deletedAssets.length"
        class="text-center text-sm leading-5 font-medium text-gray-700 sm:mt-px sm:pt-2 mb-4"
      >
        Deleted Assets
      </div>
      <AssetCard
        v-for="asset in deletedAssets"
        :key="asset.id"
        :asset="asset"
        @restoreAsset="restoreAsset"
      />
    </div>

  </div>
</template>


